import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SettingsContext = createContext(undefined);

// Symbols for the currencies we expose in the header switcher.
export const CURRENCY_SYMBOLS = {
  INR: '₹', USD: '$', GBP: '£', EUR: '€', AED: 'د.إ', AUD: 'A$', CAD: 'C$', JPY: '¥',
};
// Currencies offered in the header dropdown (order matters).
export const CURRENCY_OPTIONS = ['INR', 'USD', 'GBP', 'EUR', 'AED', 'AUD', 'CAD'];

export const SettingsProvider = ({ children }) => {
  const [branding, setBranding] = useState({
    appName: "Create enterprise",
    logo: "",
    favicon: "",
    headerBgColor: "#ffffff",
    headerTextColor: "#0f172a",
    themeColor: "#3b82f6"
  });
  const [header, setHeader] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [general, setGeneral] = useState({
    appName: "Create enterprise",
    supportEmail: "info.createenterprise@gmail.com",
    supportPhone: "+91 9727597187",
    defaultCurrency: "INR",
    defaultLanguage: "en",
    timezone: "Asia/Kolkata"
  });
  const [isLoading, setIsLoading] = useState(true);

  // User-selected display currency (persisted). Defaults to the store's base.
  const [displayCurrency, setDisplayCurrencyState] = useState(
    () => (localStorage.getItem('displayCurrency') || '').toUpperCase()
  );
  // Exchange rates relative to the base currency (base => 1).
  const [rates, setRates] = useState({});

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`/api/settings/home?v=${Date.now()}`);

        if (response.ok) {
          const data = await response.json();
          const { branding: brandingData, announcements, general: generalData, integrations: integrationsData } = data;

          if (integrationsData) {
            setIntegrations(integrationsData);
          }

          if (generalData) {
            setGeneral(generalData);
          }
          if (brandingData) {
            setBranding(brandingData);

            // Update favicon only — page <title> is now owned by each page's
            // <Seo> component (react-helmet-async); overwriting document.title
            // here directly raced with Helmet and clobbered per-page titles.
            if (brandingData.favicon) {
              // Browsers won't render a multi-hundred-KB full-size image as a
              // tab icon — request a tiny resized version so it actually shows.
              let faviconUrl = brandingData.favicon;
              if (!faviconUrl.startsWith('http') && faviconUrl.includes('/uploads')) {
                faviconUrl = `${faviconUrl}?w=64`;
              }
              // Remove ALL existing icon links (index.html ships a default one).
              document.querySelectorAll("link[rel~='icon']").forEach((l) => l.remove());
              const newLink = document.createElement('link');
              newLink.rel = 'icon';
              newLink.href = faviconUrl;
              document.head.appendChild(newLink);
            }

            // Update primary color
            if (brandingData.themeColor) {
              document.documentElement.style.setProperty('--primary', brandingData.themeColor);
              document.documentElement.style.setProperty('--primary-dark', brandingData.themeColor);
            }
          }

          if (announcements) {
            setHeader(announcements);
          }
        }
      } catch (error) {
        console.error('Error fetching settings from createhome.json:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Base (store) currency comes from admin general settings.
  const baseCurrency = (general.defaultCurrency || 'INR').toUpperCase();

  // Fetch live exchange rates relative to the base currency. Re-runs if the
  // base currency changes. Rates are cached for the session in localStorage.
  useEffect(() => {
    let cancelled = false;
    const loadRates = async () => {
      const cacheKey = `fxRates_${baseCurrency}`;
      try {
        const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
        // Use cache if it's fresh (< 12h old).
        if (cached && cached.rates && Date.now() - cached.ts < 12 * 60 * 60 * 1000) {
          setRates(cached.rates);
          return;
        }
      } catch (_) {}
      try {
        const res = await fetch(`https://open.er-api.com/v6/latest/${baseCurrency}`);
        const data = await res.json();
        if (!cancelled && data && data.rates) {
          setRates(data.rates);
          try { localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), rates: data.rates })); } catch (_) {}
        }
      } catch (e) {
        // Offline / blocked: fall back to 1:1 (no conversion).
        if (!cancelled) setRates({ [baseCurrency]: 1 });
      }
    };
    loadRates();
    return () => { cancelled = true; };
  }, [baseCurrency]);

  // Effective display currency: user choice, else base.
  const displayCode = (displayCurrency || baseCurrency).toUpperCase();
  const setDisplayCurrency = useCallback((code) => {
    const c = (code || '').toUpperCase();
    setDisplayCurrencyState(c);
    try { localStorage.setItem('displayCurrency', c); } catch (_) {}
  }, []);

  // Conversion factor from base -> display currency.
  const rate = displayCode === baseCurrency ? 1 : (Number(rates[displayCode]) || 1);

  const currencyCode = displayCode;
  const currencySymbol = CURRENCY_SYMBOLS[displayCode] || displayCode;

  // Convert a base-currency amount into the display currency (returns Number).
  const convertPrice = useCallback((amount) => {
    const n = parseFloat(String(amount ?? '').replace(/[^0-9.]/g, ''));
    if (isNaN(n)) return 0;
    return n * rate;
  }, [rate]);

  // Convert + format with the display currency symbol (returns String).
  const formatPrice = useCallback((amount, opts = {}) => {
    const v = convertPrice(amount);
    const { minimumFractionDigits = 2, maximumFractionDigits = 2 } = opts;
    return `${currencySymbol}${v.toLocaleString(undefined, { minimumFractionDigits, maximumFractionDigits })}`;
  }, [convertPrice, currencySymbol]);

  return (
    <SettingsContext.Provider value={{
      branding, header, general, integrations, isLoading,
      baseCurrency, currencyCode, currencySymbol,
      displayCurrency: displayCode, setDisplayCurrency,
      rate, convertPrice, formatPrice,
      currencyOptions: CURRENCY_OPTIONS,
    }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};